/*
 * Copyright (c) Dito Technologies
 */

import React, {Dispatch} from "react";
import styles from "./StaffSelectPageTools.module.scss";
import Button from "ui/kit/buttons/Button";
import {faCheck, faTrash, faUpload} from "@fortawesome/free-solid-svg-icons";
import classnames from "classnames";
import {
    Action,
    AddDeletedBarAction,
    BarFinderUserConfirmationAddErrorPageIdAction,
    BarFinderUserConfirmationAddLoadingPageIdAction,
    BarFinderUserConfirmationAddSuccessPageIdAction,
    State
} from "ui/feature/root/FormReducer";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import BarFinderUserConfirmation from "mutation/BarFinderUserConfirmation";
import {
    StaffSelectPageTools_sheet$data
} from "ui/feature/root/staff-select/__generated__/StaffSelectPageTools_sheet.graphql";

interface Props {
    className?: string;
    state: State;
    dispatch: Dispatch<Action>;
    page: StaffSelectPageTools_sheet$data
}

const StaffSelectPageTools: React.FC<Props> = ({dispatch, className, page, state}) => {

    const isLoading: boolean = React.useMemo(() => {
        if (!page.bar_finder_steps) {
            return true;
        }
        const step = page.bar_finder_steps[0];
        return !step.data
    }, [page]);

    const allBarIds = React.useMemo(() => {
        if (page.bar_finder_steps) {
            const set = new Set<String>();
            for (const step of page.bar_finder_steps) {
                const bars = step.data && step.data.bars;
                if (bars) {
                    for (const bar of bars) {
                        set.add(bar.bar_id)
                    }
                }
            }
            return set;
        }
        return new Set<String>();
    }, [page]);

    const hasBar = React.useMemo(() => {
        const selectedBar = state.selectedBar;
        if (selectedBar) {
            return allBarIds.has(selectedBar)
        }
    }, [allBarIds, state.selectedBar]);

    const onDeletePressed = React.useCallback(() => {
        const action: AddDeletedBarAction = {
            type: "add_deleted_bar"
        };
        dispatch(action);
    }, [dispatch]);

    const onSubmit = React.useCallback(async () => {
        const parseSteps = page.bar_finder_user_confirmation_steps
        if (!parseSteps) {
            throw new Error("should have parse steps");
        }
        const parseStep = parseSteps[0];
        const action: BarFinderUserConfirmationAddLoadingPageIdAction = {
            type: "bar_finder_user_confirmation_add_loading_page_id",
            page_id: page.id
        }
        dispatch(action);
        const deletedBarsForThisPage = state.deletedBars.filter(id => allBarIds.has(id))
        try {
            await BarFinderUserConfirmation(parseStep.id, page.id, deletedBarsForThisPage);
            const action: BarFinderUserConfirmationAddSuccessPageIdAction = {
                type: "bar_finder_user_confirmation_add_success_page_id",
                page_id: page.id
            };
            dispatch(action);
        } catch (e) {
            const action: BarFinderUserConfirmationAddErrorPageIdAction = {
                type: "bar_finder_user_confirmation_add_error_page_id",
                page_id: page.id
            };
            dispatch(action);
        }
    }, [page.bar_finder_user_confirmation_steps, page.id, dispatch, state.deletedBars, allBarIds]);

    const markCompleteState: "loading" | "error" | "not_started" | "success" = React.useMemo(() => {
        if (state.barFinderUserConfirmationLoadingPageIds.includes(page.id)) {
            return "loading";
        } else if (state.barFinderUserConfirmationSuccessPageIds.includes(page.id)) {
            return "success";
        } else if (state.barFinderUserConfirmationErrorPageIds.includes(page.id)) {
            return "error"
        }
        return "not_started";
    }, [state, page.id])

    const markCompleteButtonTitle = React.useMemo(() => {
        switch (markCompleteState) {
            case "success":
                return "Done!"
            case "loading":
                return "Loading..."
            case "error":
                return "Error, try again"
            case "not_started":
                return "Mark Complete"
        }
    }, [markCompleteState])

    const buttonsDisabled: boolean = React.useMemo(() => {
        switch (markCompleteState) {
            case "success":
            case "loading":
                return true
            default:
                return false
        }
    }, [markCompleteState]);

    const markCompleteButtonIcon = React.useMemo(() => {
        switch (markCompleteState) {
            case "success":
                return faCheck;
            default:
                return faUpload
        }
    }, [markCompleteState])

    if (isLoading) {
        return null;
    }

    return (
        <div className={classnames(styles.container, className)}>
            {markCompleteState !== "success" ?
                <div className={classnames(styles.buttonWrapper)}>
                    <Button
                        onClick={onDeletePressed}
                        disabled={!hasBar || buttonsDisabled}
                        size={"small"}
                        icon={faTrash}
                        title={"Delete Staff"}/>
                </div> : <div/>
            }
            <div className={classnames(styles.buttonWrapper)}>
                <Button onClick={onSubmit} disabled={buttonsDisabled} title={markCompleteButtonTitle}
                        icon={markCompleteButtonIcon} size={"small"}/>
            </div>
        </div>
    )
}

export default createFragmentContainer(StaffSelectPageTools, {
    page: graphql`
    fragment StaffSelectPageTools_sheet on MusicSheet {
        id
        bar_finder_user_confirmation_steps: steps(step_type: BAR_FINDER_USER_CONFIRMATION) {
            id
        }
        bar_finder_steps: steps(step_type: BAR_FINDER) {
            id 
            data {
                ... on MusicParseStepDataBarFinder {
                    bars {
                        bar_id: mask_storage_name
                    }
                }
            }
        }
    }
    `
});
